/**
 * Transpose session state — the ONE Ableton transpose, plus the guitar's
 * drop tuning, shared across every Transpose and Capo item.
 *
 * There is a single concert transpose per session (what OP-AbletonTranspose
 * is set to). Transpose items and Capo items are two linked *views* of it:
 *
 *   - Transpose row shows / sets the concert transpose directly.
 *   - Capo row shows / sets it offset by the guitar's drop tuning:
 *       capo fret      = concertTranspose - dropTuning   (= transpose + |drop|)
 *       concertTranspose = capo fret     + dropTuning
 *
 * Drop tuning is 0..-4 (0 = standard, -1 = Eb, -2 = D, ...). Example: on an
 * Eb guitar (drop -1), concert transpose 0 corresponds to capo +1, so
 * pressing 0 on a Transpose row lights +1 on every Capo row, and pressing
 * +3 on a Capo row sets the concert transpose to +2 and sends that.
 *
 * The concert transpose is persisted per-device in localStorage, and setting
 * it dispatches the OSC to OP-AbletonTranspose via {@link sendTranspose}.
 *
 * The drop tuning lives on the PROJECT (synced) since 2026-09-28. It was
 * per-device like the transpose, so Eb set on the laptop left the iPad at
 * standard: a transpose -1 song lit capo 0 on the laptop and capo -1 - off
 * the row, nothing lit - on the iPad (operator report, same day). The old
 * local value is still read as the fallback for a project that has none,
 * and carried into the project once by {@link installDropTuningCarryOver}.
 */

import { createEffect, createSignal } from 'solid-js';
import { sendTranspose, fanoutTargets } from '../opbridge/dispatch.ts';
import { activeProject, setProjectDropTuning } from '../layout/projectStore.ts';

const CONCERT_KEY = 'opbuttons.concertTranspose.v1';
const DROP_KEY = 'opbuttons.dropTuning.v1';

function loadInt(key: string, dflt: number, min: number, max: number): number {
  if (typeof localStorage === 'undefined') return dflt;
  const raw = localStorage.getItem(key);
  if (raw === null) return dflt;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return dflt;
  return Math.max(min, Math.min(max, n));
}

function persistInt(key: string, v: number): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(key, String(v)); } catch { /* private mode / quota */ }
}

const [concertTransposeSig, setConcertSig] = createSignal(loadInt(CONCERT_KEY, 0, -12, 12));
/** The concert transpose in semitones (-12..+12) — the value Ableton is
 *  transposed to, and what a Transpose row's active cell reflects. */
export const concertTranspose = concertTransposeSig;

/** This device's pre-2026-09-28 value: the fallback, never the authority. */
const [localDropSig, setLocalDropSig] = createSignal(loadInt(DROP_KEY, 0, -4, 0));

function clampDrop(d: number): number {
  return Math.max(-4, Math.min(0, Math.round(d)));
}

/** Guitar drop tuning in semitones (-4..0). 0 = standard, -1 = Eb. The
 *  active project's value; this device's old local value if it has none. */
export function dropTuning(): number {
  const p = activeProject();
  return typeof p?.dropTuning === 'number' ? clampDrop(p.dropTuning) : localDropSig();
}
const dropTuningSig = dropTuning;

/**
 * Carry this device's old local drop tuning into a project that has none.
 * Only a NON-ZERO value is carried: 0 is the default every untouched device
 * holds, so carrying it would let an iPad that was never set write
 * "standard" over the laptop's Eb in the race to sync first. Call once at boot.
 */
export function installDropTuningCarryOver(): void {
  createEffect(() => {
    const p = activeProject();
    const local = localDropSig();
    if (p && p.dropTuning === undefined && local !== 0) setProjectDropTuning(local);
  });
}

/**
 * Set the concert transpose and dispatch it to OP-AbletonTranspose. Clamped
 * -12..+12, persisted. Called by both Transpose and Capo cell taps (the Capo
 * maps its fret through {@link transposeForCapo} first).
 *
 * `bridgeIds` is the CALLING item's own multi-select bridge affinity
 * (2026-08-13) — the concert-transpose VALUE is session-global, but each
 * Transpose/Capo row can still fan its own dispatch out to different
 * bridges, since `sendTranspose` already takes a per-call target. Absent
 * (or omitted) resolves to the default bridge.
 */
export function setConcertTranspose(n: number, bridgeIds?: string[]): void {
  const v = Math.max(-12, Math.min(12, Math.round(n)));
  setConcertSig(v);
  persistInt(CONCERT_KEY, v);
  setLastSendSig({ value: v, at: Date.now() });
  for (const target of fanoutTargets(bridgeIds)) void sendTranspose(v, target);
}

/**
 * The last transpose WE actually dispatched, with when. `null` until this
 * page sends one.
 *
 * # Why this exists: "pending" was being inferred from a persisted latch
 *
 * Operator, 2026-09-21, after the capo repoint landed: *"capo 3 and transpose
 * +2 seem to be semi highlighted"* - on cells nobody had pressed. Both rows
 * derived `pending` as *"the latch disagrees with the readback"*:
 *
 *     const pending = () => observed() !== concertTranspose() ? concertTranspose() : null
 *
 * That was fine while the lit cell ALSO came from the latch, because the two
 * could only disagree mid-send. Once `cf2b6b2` pointed the lit cell at the
 * setlist readback, the readback started changing every song while the latch
 * only ever changed on a tap - so they disagree almost always, and a cell
 * nobody touched claims to be an unconfirmed send. **`concertTranspose` is
 * hydrated from `localStorage` at boot, so the phantom survived restarts and
 * predated the session that displayed it.** The operator's two values were
 * arithmetically consistent with ONE latch at +2 (capo = 2 - (-1) = 3), which
 * is what identified the single shared cause rather than two coincidences.
 *
 * A latch is a VALUE. A pending send is an EVENT - it needs a time, and only
 * a real dispatch may create one.
 *
 * # The lifecycle, and why there is no timeout
 *
 * A send stops being pending when the readback AGREES (the value comparison
 * clears it) or when a readback NEWER than the send DISAGREES - the rig moved
 * on without us, so the send is dead rather than outstanding. It is otherwise
 * left standing, deliberately: with no setlist authority at all `observed()`
 * is permanently null, and then this is the ONLY feedback that a tap did
 * anything. A timeout there would erase the one honest signal on the row.
 *
 * ⚠️ Note that OPBridge's `last_sent` reports what OPSETLIST sent, not what
 * we send, so a manual tap is never confirmed by it and correctly reads as
 * pending until the next song change supersedes it. That is the truth about
 * the rig, not a defect: the row's lit cell means "what the setlist put in
 * Live", and our tap genuinely has no authority behind it.
 */
const [lastSendSig, setLastSendSig] = createSignal<{ value: number; at: number } | null>(null);
export const lastTransposeSend = lastSendSig;

/** Drop the recorded send. Tests + sign-out. */
export function resetLastTransposeSend(): void {
  setLastSendSig(null);
}

/**
 * Set the global drop tuning (-4..0), persisted. Does NOT change the concert
 * transpose or re-send — the song's key is unchanged; only the capo mapping
 * shifts, so every Capo row re-lights against the same concert transpose.
 */
export function setDropTuning(d: number): void {
  const v = clampDrop(d);
  setLocalDropSig(v);
  persistInt(DROP_KEY, v);
  setProjectDropTuning(v);
}

/** Capo fret for the current concert transpose: `transpose - dropTuning`. */
export function capoForTranspose(t: number): number {
  return t - dropTuningSig();
}

/** Concert transpose for a capo fret: `capo + dropTuning`. */
export function transposeForCapo(c: number): number {
  return c + dropTuningSig();
}
